import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { RegistrationCoreComponent } from '../shared/registration-core/registration-core.component';
import { RegistrationCoreConfig } from '../shared/registration/registration-core-config.interface';

/** Hardcoded allowlist of supported clientId values. */
const ALLOWED_CLIENT_IDS = ['ttpoi-web', 'celcomdigi', 'maxis'] as const;

/**
 * Chrome-less shell for the `/registration/landing` route.
 * Renders only the registration core (no header, footer, onboarding, step bar, or upload).
 * Validates the `clientId` query param against the known allowlist before rendering the form.
 */
@Component({
  selector: 'app-embed-shell',
  standalone: true,
  imports: [RegistrationCoreComponent],
  template: `
    @if (error) {
      <div class="embed-error">{{ error }}</div>
    } @else if (coreConfig) {
      <app-registration-core [config]="coreConfig" />
    }
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      min-height: 100vh;
    }

    .embed-error {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 200px;
      padding: 2rem;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 1rem;
      color: #dc3545;
      text-align: center;
    }
  `],
})
export class EmbedShellComponent implements OnInit {
  private route = inject(ActivatedRoute);

  /** Error message displayed when clientId is missing or invalid. */
  error: string | null = null;

  /** Configuration passed to the registration core when clientId is valid. */
  coreConfig: RegistrationCoreConfig | null = null;

  ngOnInit(): void {
    const params = this.route.snapshot.queryParams;

    // Read query params
    const clientId = params['clientId'] ?? null;
    const theme = params['theme'] || undefined;

    // Validate clientId
    if (!clientId) {
      this.error = 'A clientId is required';
      return;
    }

    const normalizedClientId = clientId.toLowerCase();
    if (!ALLOWED_CLIENT_IDS.includes(normalizedClientId as any)) {
      this.error = 'Invalid clientId';
      return;
    }

    // Build config for the registration core
    this.coreConfig = {
      clientId: normalizedClientId,
      isLanding: true,
      theme,
      useOverlayPolicies: true,
    };
  }
}
